import React from "react";
import { Card, Alert } from 'react-bootstrap'
import { IRate, IPaymentChoice, IRealTimeAutoResult } from "../classes/Domain";
import CoveragesOverview from '../components/CoveragesOverview';
import AlternateCoverageBanner from '../components/AlternateCoverageBanner';
import PaymentChoice from '../components/PaymentChoice';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from "swiper";
import SwiperCore, { Pagination } from 'swiper';
import Utilities from "../classes/Utilities";
import RateErrorHandler from "./RateErrors/RateErrorHandler";
import "react-toggle/style.css"
import * as _ from 'lodash';
import Collapsible from 'react-collapsible';

interface IRateResultProps {
    rateResult: IRate,
    currentRate: IRealTimeAutoResult | null | undefined,
    index: number,
    onPaymentChoiceSelected(paymentChoice: IPaymentChoice): void,
    reRate?: () => void;
    /** Req #7: offer the opposite package (LO ↔ full coverage $1,000) and re-rate on Yes. */
    alternateCoverage?: {
        isLiabilityOnly: boolean;
        onYes: () => void;
        disabled?: boolean;
    };
}

interface IState {}

class RateResult extends React.Component<IRateResultProps, IState> {
    private _swiper: SwiperClass;

	constructor(props: IRateResultProps) {
        super(props);

        this.state = {};

        this.onPaymentChoiceSelected = this.onPaymentChoiceSelected.bind(this);
        this.renderBullet = this.renderBullet.bind(this);
        this._swiper = new SwiperClass(".swiper-container");

        SwiperCore.use([Pagination]);
    }

    getValueSafe(obj: any, property: string) {
        return obj ? obj[property] : "";
    }

    componentDidMount() {
        this.setState({ ...this.state });
    }

    componentDidUpdate(prevProps: IRateResultProps) {
        if (
            prevProps.currentRate !== this.props.currentRate ||
            JSON.stringify(prevProps.rateResult.paymentChoices) !== JSON.stringify(this.props.rateResult.paymentChoices)
        ) {
            setTimeout(() => {
                this._swiper?.updateAutoHeight(100);
            }, 50);
        }
    }

    onPaymentChoiceSelected(paymentChoice: IPaymentChoice) {
        this.props.onPaymentChoiceSelected(paymentChoice);
    }

    IsOpen(program: string) {
        return this.props.rateResult.paymentChoices.filter(x => x.program === program).some(x => x.selected);
    }

    renderBullet(index: number, className: string) {
        if (0 === index && 0 < this.props.rateResult.paymentChoices.length) {
            return '<div class="' + className + '"><i class=\"fas fa-money-check-alt\"></i><span class=\"bullet-caption\">Payment Plans</span></div>';
        }

        //var vehicleRates = _.filter(this.props.rateResult.rate.vehicleRates, (vr) => !!vr.make);

        //if (1 + vehicleRates.length === index && this.props.rateResult.rate.messageMemo) {
        //    return '<div class="' + className + '"><i class=\"far fa-comment-dots\"></i><div class=\"bullet-caption\">Message</div></div>';
        //}

        if (0 < index && 0 < _.filter(this.props.currentRate?.vehicleRates, (vr) => !!vr.make).length) {
            return '<div class="' + className + '"><i class=\"fas fa-car\"></i><span class=\"bullet-caption\">Coverage Details</span></div>';
        }

        return "";
    }

    render() {
        const choicesByProgram = _.groupBy(this.props.rateResult.paymentChoices, 'program');
        const uniquePrograms = Object.keys(choicesByProgram);
        const hasMultiplePrograms = uniquePrograms.length > 1;
        const hasPaymentChoices = this.props.rateResult.paymentChoices && this.props.rateResult.paymentChoices.length > 0;

        return (
            <Card className="rtr-rate-result">
                <Card.Body>

                    <Swiper
                        spaceBetween={20}
                        slidesPerView={"auto"}
                        centeredSlides={true}
                        autoHeight={true}
                        pagination={{
                            type: "bullets",
                            renderBullet: this.renderBullet,
                            clickable: true
                        }}
                        onInit={(swiper) => { this._swiper = swiper; }}
                    >
                        {hasPaymentChoices && (
                            <SwiperSlide key="payment-choices-slide">
                                <div className="payment-choices-container text-left px-2">

                                    <div className="payment-header-block mb-4">
                                        <h3 className="payment-main-title mb-1">Choose a payment option for your 6-month auto insurance</h3>
                                    </div>

                                    <div className="payment-choices-list">
                                        {uniquePrograms.map((programName) => {
                                            const sortedChoices = choicesByProgram[programName].sort(
                                                (a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0)
                                            );

                                            const displayTitle = programName === 'SMC-OK-OA-P' ? 'OK Premier' : 'OK Classic';

                                            const renderedCards = sortedChoices.map((paymentChoice, index) => (
                                                <PaymentChoice
                                                    key={`${paymentChoice.program}-${paymentChoice.payTermCd}-${paymentChoice.payPlanDesc}-${paymentChoice.firstPay}-${paymentChoice.regularPay}-${index}`}
                                                    paymentChoice={paymentChoice}
                                                    isMostPopular={index === 0}
                                                    onSelected={() => this.onPaymentChoiceSelected(paymentChoice)}
                                                />
                                            ));

                                            if (hasMultiplePrograms) {
                                                return (
                                                    <Collapsible
                                                        key={programName}
                                                        trigger={displayTitle}
                                                        open={this.IsOpen(programName)}
                                                        onOpen={() => { this._swiper?.updateAutoHeight(100); }}
                                                        onClose={() => { this._swiper?.updateAutoHeight(100); }}
                                                    >
                                                        {renderedCards}
                                                    </Collapsible>
                                                );
                                            }

                                            return (
                                                <div key={programName} className="single-program-wrapper">
                                                    {renderedCards}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {this.props.alternateCoverage && (
                                        <AlternateCoverageBanner
                                            isLiabilityOnly={this.props.alternateCoverage.isLiabilityOnly}
                                            onYes={this.props.alternateCoverage.onYes}
                                            disabled={this.props.alternateCoverage.disabled}
                                        />
                                    )}

                                </div>
                            </SwiperSlide>
                        )}

                        <SwiperSlide key="coverages-overview-slide">
                            <CoveragesOverview
                                currentRate={this.props.currentRate}
                                alternateCoverageBanner={this.props.alternateCoverage
                                    ? <AlternateCoverageBanner
                                        isLiabilityOnly={this.props.alternateCoverage.isLiabilityOnly}
                                        onYes={this.props.alternateCoverage.onYes}
                                        disabled={this.props.alternateCoverage.disabled}
                                    />
                                    : undefined}
                            />
                        </SwiperSlide>

                    </Swiper>
                </Card.Body>
            </Card>
        );
    }
}

export default RateResult;